import type { DriverAvailability } from "@tdelivr/api-client/generated/driver/driver-availability.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { absent, at, field, optional, type RecordDetail } from "./record-detail.ts";

/**
 * What the driver roster does to the rows DRV-05 and DRV-01 return, away from any rendering.
 *
 * The same split as `demand-queue.ts`, `offer-monitor.ts` and `live-map.ts`, for the same reason:
 * deciding what a driver's state actually is, joining a name to it, counting the three tiles,
 * searching, ordering and paging can all be stated and tested without a browser.
 *
 * **This page is two services and one key, and until recently the key was two names.** DRV-01 keyed
 * its table on `profile_id` and published the driver as `id` while DRV-03, DRV-05, DRV-06 and
 * DRV-07 all published `driverId` — so a roster putting names against availability rows was
 * asserting a relationship no contract stated. Document 07 §14 settled the name and
 * `tests/contract/driver-identifier.test.ts` holds it for the whole domain. The join is now
 * published on both sides: `DriverProfile.driverId` and `DriverAvailability.driverId`, both
 * required, both read from the same `driver_id` column by their own repositories.
 *
 * **The thing this screen must not do is draw an unconfirmable state as a confident one.** DRV-05
 * publishes `fresh` and `dispatchable` as its own judgement, and `presenceFor()` below is the whole
 * of the rule. R-18 in `docs/design/DESIGN_RECONCILIATION.md` is the measurement behind it.
 */

/** One row of DRV-05's roster: the published availability plus its two judgements. */
export type RosterRow = DriverAvailability & {
  readonly fresh: boolean;
  readonly dispatchable: boolean;
};

/**
 * The four states the design's roster draws, in its own words.
 *
 * `05 Dispatch Live Map.dc.html` states them as `dState`: `stale`, `job`, `available`, `offline`.
 * Named here as the design names them so a reader can find the line this came from.
 */
export type RosterPresence = "available" | "job" | "stale" | "offline";

export interface PresenceLook {
  /** The state class the badge borrows its glyph and hue from. */
  readonly classKey: StateClassKey;
  /** The word on the badge. A state is never told by colour alone. */
  readonly word: string;
}

/**
 * How each of the four is drawn, from the design file's own `dState` and one correction.
 *
 * The design writes `stale → ['unknown','stale location']`, `job → ['active','on a job']`,
 * `available → ['done','available']`, `offline → ['closed','offline']`. Three are taken verbatim.
 * Two things differ and both are recorded rather than decided quietly:
 *
 * **`stale location` loses its second word.** The design's roster read DRV-06 location freshness,
 * because G-13 was open and DRV-05 answered `drivers: unknown[]`. G-13 is closed and this page
 * reads DRV-05, which publishes `fresh` — whether the *online lease* has lapsed — and publishes no
 * location at all, under scopes `navigation.ts` does not grant this page. So the row says `stale`,
 * which is true of a lapsed lease, rather than `stale location`, which would claim a position this
 * screen has never been told. The design's own tile label for the same set is already `stale`.
 *
 * **`on a job` takes the `unknown` class rather than `active`.** Not a change of taste: it is the
 * live map's own rule applied where it bites hardest. `live-map.ts` draws a confirmed job — one
 * TRP-02 named — solid, and an inferred one dashed, because the only evidence for an inferred job
 * is DRV-05 withholding the driver from supply, and a driver on a break produces exactly that. This
 * page has no trip source whatsoever, so **every** `on a job` row here is that inference. Drawing
 * them with the design's confident blue would state as fact the one thing R-09 established the
 * platform cannot yet confirm. The word stays the design's.
 */
export const PRESENCE_LOOKS: Readonly<Record<RosterPresence, PresenceLook>> = {
  available: { classKey: "done", word: "available" },
  job: { classKey: "unknown", word: "on a job" },
  stale: { classKey: "unknown", word: "stale" },
  offline: { classKey: "closed", word: "offline" },
};

/**
 * Which of the four a row is in, from the fields DRV-05 publishes.
 *
 * The order of the tests is the whole correctness of this function.
 *
 * `presence` is asked first. An offline driver has no lease, so DRV-05 answers `fresh: false` for
 * them — and a roster testing freshness first would report every driver who has signed off as a
 * driver whose app has gone silent, which is the one thing this board exists to point at.
 *
 * `fresh` is asked second, before `dispatchable`. A row still saying `online` whose lease lapsed is
 * a driver whose app stopped reporting: an offer sent to them goes into silence, and nothing else
 * on the page points at them. DRV-05's listing is ordered stalest-first for exactly this reason, so
 * they are already at the top; this is what makes them say so.
 *
 * `dispatchable` is DRV-05's own judgement — a fresh online lease and `availability: available` —
 * so by this point it reduces to `availability`, and it is read rather than reconstructed because
 * the lease rule is the service's to change.
 */
export function presenceFor(row: RosterRow): RosterPresence {
  if (row.presence === "offline") return "offline";
  if (!row.fresh) return "stale";
  if (!row.dispatchable) return "job";
  return "available";
}

/**
 * Why the badge says what it says, for the two rows where the word is not the whole story.
 *
 * On the badge rather than in a column of its own, which is the treatment the offer monitor settled
 * on: a column the design does not draw is design content invented here, and an operator who
 * wonders about a row gets the answer where they are already pointing.
 *
 * `undefined` for a row whose word needs nothing added, so the badge carries no empty tooltip.
 */
export function presenceReason(row: RosterRow): string | undefined {
  switch (presenceFor(row)) {
    case "stale":
      return (
        `DRV-05 still records presence online, and the online lease has lapsed` +
        `${row.leaseExpiresAt === undefined ? "" : ` at ${row.leaseExpiresAt}`}. ` +
        `The driver's app has stopped reporting; an offer sent now goes into silence.`
      );
    case "job":
      // The over-claim R-09 made deliberately, said out loud on every row that carries it.
      return (
        `Inferred. DRV-05 is withholding this driver from supply with reasonCode ` +
        `${row.reasonCode}, and no listing on this platform says which assignment holds them — ` +
        `a driver on a break answers identically. See R-09.`
      );
    case "available":
    case "offline":
      return undefined;
  }
}

/**
 * Seconds since the driver's app last reported, or `undefined` when it never has.
 *
 * `lastHeartbeatAt` and nothing else. `updatedAt` is when the row last changed, which for an
 * offline driver is when they signed off and for an online one is whatever last wrote the row —
 * a different quantity, and folding two quantities into one column is how a board grows a number
 * an operator works. A driver who has never been online has no heartbeat, and the cell is empty
 * rather than filled from the nearest timestamp to hand.
 */
export function reportedAgeSeconds(row: RosterRow, now: number): number | undefined {
  if (row.lastHeartbeatAt === undefined) return undefined;
  const at = Date.parse(row.lastHeartbeatAt);
  if (!Number.isFinite(at)) return undefined;
  return Math.max(0, Math.round((now - at) / 1000));
}

/**
 * The age cell, in the design's own two spellings.
 *
 * `05 Dispatch Live Map.dc.html` writes `mins(d[5])+'m'` for a stale row and `d[5]+'s'` for every
 * other, so minutes once the row is in doubt and seconds while it is not. The threshold is the
 * state rather than the number, which is the design's own reading and the better one: a fresh row
 * at 70 seconds is fine and a lapsed row at 70 seconds is the alarm.
 *
 * The design draws an age on every row because DRV-06 answers one for every fix. DRV-05 does not,
 * so an absent heartbeat renders the designed empty treatment: nothing.
 */
export function reportedAgeText(row: RosterRow, now: number): string {
  const seconds = reportedAgeSeconds(row, now);
  if (seconds === undefined) return "";
  return presenceFor(row) === "stale" ? `${Math.round(seconds / 60)}m` : `${seconds}s`;
}

/**
 * The initials in the row's avatar, by the design's own expression.
 *
 * `d[1].split(' ').map(x=>x[0]).join('').replace('.','')` — so `M. Achieng` becomes `MA` and
 * `P. Njoroge` becomes `PN`. Copied rather than reinvented, including the single `replace` that
 * strips one full stop and not every one.
 */
export function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0] ?? "")
    .join("")
    .replace(".", "");
}

/**
 * The design's driver cell: the name where DRV-01 answered one, the identifier where it did not.
 *
 * The same degradation the offer monitor draws, for the same reason. DRV-01 is a second service
 * under a second pair of scopes, and although `navigation.ts` grants this page both pairs, an
 * operator may hold one and not the other — and DRV-01's listing is capped at 200 rows and answers
 * `active` profiles only, so a large tenant has availability rows with no name on this page even
 * when both reads succeed. An identifier is what the design's cell degrades to; a blank is not.
 */
export function driverName(row: RosterRow, names: ReadonlyMap<string, string>): string | undefined {
  return names.get(row.driverId);
}

/** The three tiles the design's roster panel draws, over the rows the board holds. */
export interface RosterCounts {
  readonly available: number;
  readonly job: number;
  readonly stale: number;
}

/**
 * Count the three, exactly as the design's `cnt` does.
 *
 * `offline` has no tile. The design draws three — `available`, `on a job`, `stale` — and a fourth
 * would be a tile this screen invented. It is also the one count that would be wrong by
 * construction: DRV-05's listing answers online drivers unless asked otherwise, so an offline count
 * taken from an unfiltered page is always nought.
 */
export function rosterCounts(rows: readonly RosterRow[]): RosterCounts {
  let available = 0;
  let job = 0;
  let stale = 0;
  for (const row of rows) {
    const presence = presenceFor(row);
    if (presence === "available") available += 1;
    else if (presence === "job") job += 1;
    else if (presence === "stale") stale += 1;
  }
  return { available, job, stale };
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type RosterOrder = "default" | "state" | "az" | "za";

export interface RosterQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  readonly order: RosterOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
  /** Now, so the age cell and a search for `12s` agree with each other. */
  readonly now: number;
  /** DRV-01's `displayName` by `driverId`, so a search matches the name an operator can see. */
  readonly names: ReadonlyMap<string, string>;
}

export interface RosterPage {
  /** The rows on the current page. */
  readonly rows: readonly RosterRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly RosterRow[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row that a search should match, including the words drawn rather than published. */
function searchableText(row: RosterRow, query: RosterQuery): string {
  const presence = presenceFor(row);
  return [
    row.driverId,
    driverName(row, query.names) ?? "",
    row.presence,
    row.availability,
    row.reasonCode,
    presence,
    PRESENCE_LOOKS[presence].word,
    reportedAgeText(row, query.now),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order DRV-05 answered in — `ORDER BY updated_at, driver_id`, stalest first — and
 * the screen must not quietly replace it. Both the service's own listing summary and R-01 say why:
 * a row still saying `online` whose lease lapsed an hour ago is the one thing on this board that
 * nothing else points at, and a roster re-sorted by name buries it among the drivers who are fine.
 *
 * The design's roster panel re-sorts its twelve fixed rows `stale, available, job`, which is the
 * same intent reached a different way — and over a keyset-paged listing it cannot be done, because
 * page two of a browser-side re-sort is not page two of anything. DRV-05's ordering already puts
 * the stale rows first, so `default` is both the service's answer and the design's.
 *
 * Neither filter is here. `presence` and `availability` go to DRV-05 as query parameters, because
 * its unfiltered page is online drivers only and the four offline states are not in it at any page
 * — the same trap the offer monitor's state filter avoids. This function sees only what came back.
 */
export function rosterPage(rows: readonly RosterRow[], query: RosterQuery): RosterPage {
  const needle = query.search.trim().toLowerCase();
  const matched =
    needle.length === 0
      ? [...rows]
      : rows.filter((row) => searchableText(row, query).includes(needle));

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return PRESENCE_LOOKS[presenceFor(left)].word.localeCompare(
              PRESENCE_LOOKS[presenceFor(right)].word,
            );
          }
          // "Name" is the first column, which on this board is the driver — the name where there is
          // one, so A–Z orders what the operator can read rather than the identifiers behind it.
          const compared = (driverName(left, query.names) ?? left.driverId).localeCompare(
            driverName(right, query.names) ?? right.driverId,
          );
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The one sentence this board owes an operator about what it actually fetched, or nothing.
 *
 * The same line the demand queue and the offer monitor draw, and here it carries two facts rather
 * than one, because this board's default is a filter nobody chose.
 *
 * Truncation wins, because a partial page is the stronger claim: 200 of 600 drivers read as all of
 * them is worse than a default whose shape has gone unnoticed.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param truncated whether DRV-05 answered a cursor, meaning this is the head of the list.
 * @param presence the presence the operator chose, or `undefined` for the service's own default.
 * @param perFetch the page size asked for.
 */
export function boundedNote(
  fetched: number | undefined,
  truncated: boolean,
  presence: "online" | "offline" | undefined,
  perFetch: number,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (truncated) {
    return `More than ${perFetch} drivers match. This board shows the first ${perFetch}, so the counts above are floors rather than totals.`;
  }
  if (presence === undefined) {
    return "Online is DRV-05's own default, not a filter chosen here. A driver who has signed off is listed only when Offline is chosen above.";
  }
  return undefined;
}

/**
 * Whether the names half of the page is missing anybody it was asked for.
 *
 * Worth saying rather than leaving as a column of identifiers. DRV-01's listing answers `active`
 * profiles and nothing else — there is no value of its `operationalStatus` filter meaning "any" —
 * so a driver who is online while suspended has availability on this board and no name anywhere on
 * it, and so does every driver past the 200th once a tenant is large. Either way the cell degrades
 * to the identifier, which is right, and the operator is told why.
 */
export function unnamedNote(
  rows: readonly RosterRow[] | undefined,
  names: ReadonlyMap<string, string>,
  namesTruncated: boolean,
): string | undefined {
  if (rows === undefined || rows.length === 0) return undefined;
  const unnamed = rows.filter((row) => !names.has(row.driverId)).length;
  if (unnamed === 0) return undefined;
  const because = namesTruncated
    ? "DRV-01 answered more profiles than this page read"
    : "DRV-01 lists active profiles only";
  return `${unnamed} of ${rows.length} rows show an identifier instead of a name: ${because}.`;
}

/**
 * The selected driver, for the panel beside the roster.
 *
 * Every line is a field DRV-05 published on the row the board already holds, plus the name DRV-01
 * answered for the same driver — which the board joins for the table and so can hand over here
 * without a second request.
 *
 * Two absences are named rather than left blank, and both are the ones an operator reaches for
 * first on a driver they are about to dispatch. **Where the driver is** belongs to DRV-06 and the
 * roster does not read it: the live map does, and the two boards are deliberately separate reads.
 * **What they are carrying** has no source at all — R-09 measured that no listing on this platform
 * says which assignment holds a driver, which is the same absence the `on a job` badge already
 * says out loud.
 */
export function rosterDetail(
  row: RosterRow,
  names: ReadonlyMap<string, string>,
  now: number,
): RecordDetail {
  const presence = presenceFor(row);
  const look = PRESENCE_LOOKS[presence];
  const name = driverName(row, names);
  const age = reportedAgeSeconds(row, now);
  const reason = presenceReason(row);

  return {
    headline: name ?? row.driverId,
    identifier: row.driverId,
    source: name === undefined ? "DRV-05" : "DRV-05 · DRV-01",
    state: {
      classKey: look.classKey,
      label: look.word,
      code: "DRV-05",
      status: row.presence,
    },
    fields: [
      field("Presence", row.presence),
      field("Availability", row.availability),
      field("Reason code", row.reasonCode, true),
      // DRV-05's own two judgements, which are what the badge is computed from. Shown because a
      // dispatcher reading a `stale` row is really asking which of the two clocks ran out.
      field("Lease fresh", row.fresh ? "yes" : "no"),
      field("Dispatchable", row.dispatchable ? "yes" : "no"),
      ...optional("Last heartbeat", at(row.lastHeartbeatAt)),
      ...(age === undefined ? [] : [field("Reported", `${String(age)}s ago`)]),
      ...optional("Lease expires", at(row.leaseExpiresAt)),
      ...optional("Lease", row.leaseId, true),
      ...optional("Online since", at(row.onlineSince)),
      ...(name === undefined
        ? [
            absent(
              "Name",
              "DRV-01 lists active profiles only, so a suspended or deactivated driver appears here with an identifier and no name.",
            ),
          ]
        : []),
      absent(
        "Last known position",
        "DRV-06 publishes it and this board does not read it — the live map does. Two boards, two reads, deliberately.",
      ),
      absent(
        "Current job",
        "Nothing on this platform publishes which assignment holds a driver. R-09 measured it, and it is why `on a job` is inferred rather than stated.",
      ),
      field("Version", `v${String(row.version)}`),
      ...optional("Last changed", at(row.updatedAt)),
    ],
    ...(reason === undefined ? {} : { note: reason }),
  };
}
